import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Minus, ArrowRight, CheckCircle2, Briefcase, Award } from 'lucide-react';
import SEO from '../components/SEO';

export default function Chocolate() {
  const [formStatus, setFormStatus] = useState('idle'); // idle, submitting, success, error

  const chocolateSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "Aalborg Chokolade med Cards by Eve Print",
    "image": "https://assets.auxodev.dk/cdn-cgi/image/width=1000,quality=85,format=auto/assets/19777474-0f63-4a2a-88a2-aa8209d1f0a9/image.png",
    "description": "Eksklusiv mørk chokolade fra Aalborg Chokolade med unikt håndtegnet kakaoprint af Cards by Eve. Perfekt som firma- og forretningsgave.",
    "brand": {
      "@type": "Brand",
      "name": "Aalborg Chokolade & Cards by Eve"
    },
    "offers": {
      "@type": "AggregateOffer",
      "priceCurrency": "DKK",
      "price": "Kontakt for pris",
      "eligibleQuantity": {
        "@type": "QuantitativeValue",
        "value": 1,
        "unitCode": "C62"
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus('submitting');
    
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());
    
    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      
      if (response.ok) {
        setFormStatus('success');
        e.target.reset();
      } else {
        setFormStatus('error');
      }
    } catch (error) {
      setFormStatus('error');
    }
  };

  return (
    <div className="bg-white">
      <SEO 
        title="Aalborg Chokolade med Kakaoprint"
        description="Eksklusiv mørk chokolade fra Aalborg Chokolade prydet med unikt, håndtegnet kakaoprint af Cards by Eve. Perfekt til firmaer, gaveæsker og forhandlere."
        schema={chocolateSchema}
      />
      {/* Hero Section */}
      <header className="pt-24 pb-20 bg-brand-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-4 mb-8">
            <Minus className="w-6 h-6 text-brand-950" />
            <span className="text-brand-400 text-xs uppercase tracking-[0.3em] font-medium">
              Kollektion / Chokolade
            </span>
          </div>
          <h1 className="text-6xl md:text-8xl font-serif text-brand-950 leading-tight mb-8">
            Aalborg <br />
            <span className="italic">Chokolade</span>
          </h1>
          <p className="text-brand-600 text-lg font-light max-w-2xl leading-relaxed">
            Udforsk min håndtegnede chokolade, trykt på en pæn mat og miljøvenlig kvalitet. Skabt med kærlighed til detaljen og det gode håndværk.
          </p>
        </div>
      </header>

      {/* Product Detail Section */}
      <section className="py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-24 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1 }}
              className="relative"
            >
              <div className="aspect-[4/5] overflow-hidden rounded-sm bg-brand-50 shadow-2xl">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=1000,quality=85,format=auto/assets/19777474-0f63-4a2a-88a2-aa8209d1f0a9/image.png"
                  alt="Luksus mørk chokolade med nødder fra Aalborg Chokolade prydet med smukt botanisk kakaoprint fra Cards by Eve"
                  className="w-full h-full object-cover"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div className="absolute -bottom-12 -right-12 w-48 h-48 bg-brand-100/50 -z-10 rounded-full blur-3xl"></div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1, delay: 0.2 }}
              className="space-y-10"
            >
              <div>
                <h2 className="text-4xl font-serif text-brand-950 mb-6 italic">Mørk med nødder & eget print</h2>
                <div className="h-px w-20 bg-brand-950 mb-8"></div>
                <p className="text-brand-600 font-light leading-relaxed text-lg">
                  Jeg elsker Aalborg Chokolade og de laver den bedste mørke chokolade med nødder. Derfor jeg spurgt om jeg må få dem lavet med mit print på og de er super fine som gaver og i gaveæsker. Hvis man har en anden yndlings derfra kan de også laves med mit print på efter aftale. Og kan leveres med matchende kort til en god pris. Leveringstiden er normalt kort.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                <div className="flex items-start space-x-4">
                  <div className="bg-brand-50 p-3 rounded-full">
                    <Award className="w-5 h-5 text-brand-950" />
                  </div>
                  <div>
                    <h4 className="font-serif text-brand-950 mb-1">Høj Kvalitet</h4>
                    <p className="text-brand-500 text-sm font-light">Fra anerkendte Aalborg Chokolade.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-brand-50 p-3 rounded-full">
                    <Briefcase className="w-5 h-5 text-brand-950" />
                  </div>
                  <div>
                    <h4 className="font-serif text-brand-950 mb-1">B2B Eksklusiv</h4>
                    <p className="text-brand-500 text-sm font-light">Laves kun på bestilling til firmaer.</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Corporate Inquiry Section */}
      <section className="py-20 bg-brand-50 text-brand-950 overflow-hidden relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <div className="max-w-3xl mx-auto">
            <span className="text-brand-400 text-xs uppercase tracking-[0.3em] font-medium block mb-2">Forespørgsel</span>
            <h2 className="text-4xl md:text-5xl font-serif mb-6 leading-tight">
              Interesseret i <span className="italic">Chokolade?</span>
            </h2>
            <p className="text-brand-600 text-lg font-light leading-relaxed mb-12">
              Udfyld formularen herunder, så kontakter jeg dig med mere information og priser til forhandlere.
            </p>
          </div>

          <div className="bg-white p-8 md:p-16 shadow-2xl max-w-4xl mx-auto text-left">
            {formStatus === 'success' ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-12"
              >
                <CheckCircle2 className="w-16 h-16 text-brand-950 mx-auto mb-8" />
                <h3 className="text-3xl font-serif text-brand-950 mb-4">Tak for din forespørgsel</h3>
                <p className="text-brand-600 font-light mb-10">Jeg vender tilbage til dig hurtigst muligt med information om priser og levering.</p>
                <button
                  onClick={() => setFormStatus('idle')}
                  className="text-brand-950 text-xs uppercase tracking-widest font-bold border-b border-brand-950 pb-2"
                >
                  Send en ny forespørgsel
                </button>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-10">
                {/* Honeypot */}
                <div className="sr-only absolute" aria-hidden="true">
                  <input type="text" name="website_url" tabIndex="-1" autoComplete="off" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                  <div className="space-y-2">
                    <label htmlFor="company" className="text-[10px] uppercase tracking-widest font-bold text-brand-400">Virksomhed</label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      required
                      className="w-full bg-transparent border-b border-brand-200 py-3 focus:border-brand-950 outline-none transition-colors font-light text-brand-950"
                      placeholder="Virksomhedens navn"
                    />
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="name" className="text-[10px] uppercase tracking-widest font-bold text-brand-400">Kontaktperson</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full bg-transparent border-b border-brand-200 py-3 focus:border-brand-950 outline-none transition-colors font-light text-brand-950"
                      placeholder="Dit navn"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                  <div className="space-y-2">
                    <label htmlFor="email" className="text-[10px] uppercase tracking-widest font-bold text-brand-400">E-mail</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full bg-transparent border-b border-brand-200 py-3 focus:border-brand-950 outline-none transition-colors font-light text-brand-950"
                      placeholder="din@email.dk"
                    />
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="quantity" className="text-[10px] uppercase tracking-widest font-bold text-brand-400">Forventet antal</label>
                    <input
                      type="text"
                      id="quantity"
                      name="quantity"
                      className="w-full bg-transparent border-b border-brand-200 py-3 focus:border-brand-950 outline-none transition-colors font-light text-brand-950"
                      placeholder="f.eks. 50 stk."
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label htmlFor="message" className="text-[10px] uppercase tracking-widest font-bold text-brand-400">Besked</label>
                  <textarea
                    id="message"
                    name="message"
                    rows="4"
                    required
                    className="w-full bg-transparent border-b border-brand-200 py-3 focus:border-brand-950 outline-none transition-colors font-light text-brand-950 resize-none"
                    placeholder="Skriv lidt om jeres ønsker..."
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={formStatus === 'submitting'}
                  className="w-full bg-brand-950 text-white py-6 text-xs uppercase tracking-widest font-bold hover:bg-brand-800 transition-all disabled:opacity-50 flex items-center justify-center group"
                >
                  {formStatus === 'submitting' ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sender...
                    </span>
                  ) : (
                    <span className="flex items-center">
                      Send forespørgsel <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </span>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
